import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, RadixTable } from '@features/ui';
import { periodOptions } from '@features/admin/reports/reportData';
import { TReportAffiliatePayoutData, ReportPeriod } from '@src/types';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import {
  exportOldAffiliatePayoutByUsers,
  getOldAffiliatePayoutByUsers,
} from '@src/services/reports';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

const buildAffiliatePayoutPayload = (
  filters: ReturnType<typeof useReportFilters>,
  minimumPayout: string
) => ({
  start_date: filters.startDate
    ? dayjs(filters.startDate).format('YYYY-MM-DD')
    : null,
  end_date: filters.endDate
    ? dayjs(filters.endDate).format('YYYY-MM-DD')
    : null,
  minimum_payout: parseFloat(minimumPayout || '0'),
});

const downloadCsvFile = (blobPart: BlobPart, name: string) => {
  const csvBlob = new Blob([blobPart], { type: 'text/csv;charset=utf-8;' });
  const csvUrl = URL.createObjectURL(csvBlob);
  const link = document.createElement('a');

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const filename = `${name}-${timestamp}.csv`;

  link.href = csvUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(csvUrl);
  document.body.removeChild(link);
};

export const OldAffiliatePayoutAmountByUsers: FC = () => {
  const [data, setData] = useState<TReportAffiliatePayoutData[]>([]);
  const [minimumPayout, setMinimumPayout] = useState<string>('0');

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_12_MONTHS,
    enableComparisons: false,
  });

  const reportRows = useMemo(
    () => data.filter((item) => !item.isTotal),
    [data]
  );

  const totalRow = useMemo(
    () => data.find((item) => item.isTotal) ?? null,
    [data]
  );

  const tableSource = useMemo(
    () => [...reportRows, ...(totalRow ? [totalRow] : [])],
    [reportRows, totalRow]
  );

  const { columns, tableData } =
    useReportTableColumns<TReportAffiliatePayoutData>({
      data: tableSource,
      getAccessField: (item) => item.userName,
      getMainDataField: () => 0,
      includeTotalsRow: false,
      customColumns: [
        {
          title: 'PAYOUT',
          className: 'text-right',
          render: (item) => {
            if (!item) return null;

            const isTotalsRow = item.isTotal || item.userName === 'TOTAL';

            return (
              <p className={isTotalsRow ? 'text-[#B6D72C]' : 'text-white'}>
                ${item.payout.toFixed(2)}
              </p>
            );
          },
        },
      ],
    });

  const fetchReport = useCallback(
    async (showSuccessToast = true) => {
      filters.setIsLoading(true);
      try {
        const payload = buildAffiliatePayoutPayload(filters, minimumPayout);
        const response = await getOldAffiliatePayoutByUsers(payload);

        setData(response);

        if (showSuccessToast) {
          toast.success('Report generated successfully!');
        }
      } catch (error) {
        console.error('Error fetching report:', error);
        toast.error('Failed to generate report');
      } finally {
        filters.setIsLoading(false);
      }
    },
    [filters, minimumPayout]
  );

  const handleRunReport = useCallback(async () => {
    await fetchReport(true);
  }, [fetchReport]);

  const handleExportReport = useCallback(async () => {
    try {
      const payload = buildAffiliatePayoutPayload(filters, minimumPayout);
      const response = await exportOldAffiliatePayoutByUsers(payload);

      downloadCsvFile(response.data, 'old-affiliate-payout-amount-by-users');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  }, [filters, minimumPayout]);

  useEffect(() => {
    fetchReport(false).then(() => null);
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Old Affiliate Payout Amount by Users
      </h1>

      <Card className="p-6">
        <div className="space-y-4">
          <ReportFilteringSettings
            dateRangeValue={filters.valueStart}
            isLoading={filters.isLoading}
            periodOptions={periodOptions}
            periodRange={filters.periodRange}
            selectedPeriod={filters.selectedPeriod}
            showGroupByFilter={false}
            showRunReportButton={false}
            onDateRangeChange={filters.setValueStart}
            onPeriodChange={filters.setSelectedPeriod}
          />

          {/* Custom Minimum Payout Field */}
          <div className="flex items-center">
            <div className="min-w-[250px]">
              <label className="text-white text-sm font-medium">
                Minimum Payout
              </label>
              <p className="text-[#999999] text-xs mt-1">
                include to report only affiliates <br />
                with payout in given period <br />
                more or equal to
              </p>
            </div>
            <div className="flex-1 flex items-center gap-2">
              <input
                className="px-3 py-2 border rounded-md text-white text-sm w-[300px] max-w-[300px]"
                max="999999"
                min="0"
                placeholder="0.00"
                step="0.01"
                type="number"
                value={minimumPayout}
                onChange={(e) => setMinimumPayout(e.target.value)}
              />
              <span className="text-[#999999] text-sm">USD</span>
            </div>
          </div>
          <div className="flex items-center">
            <div className="min-w-[250px]" />

            <div className="flex justify-end">
              <Button
                className="bg-[#B6D72C] hover:bg-[#A5C628] text-black font-semibold px-8"
                disabled={filters.isLoading}
                onClick={handleRunReport}
              >
                {filters.isLoading ? 'Loading...' : 'Run Report'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-white text-sm">
              Showing {reportRows.length} users with payout ≥ $
              {Number(minimumPayout || '0').toFixed(2)}
            </p>
            <Button
              className="bg-transparent border border-[#B6D72C] text-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              disabled={filters.isLoading || reportRows.length === 0}
              onClick={handleExportReport}
            >
              Export CSV
            </Button>
          </div>
          <RadixTable columns={columns} data={tableData} />
        </div>
      </Card>
    </div>
  );
};
